import { Controller } from 'react-hook-form';
import {
  DateInput,
  DateInputSize,
  InputSize,
  InputText,
  Select,
  SelectSize,
  SelectType,
  Textarea,
} from '@ui/src';
import InfoRow from '../../common/info-row';
import StepPartBlock from '../../common/step-part-block';
import { NewApplicationStatus } from '../../../../hoc/common/types';
import SicCodes from './sic-codes';
import CountriesOnInterests from './countries-on-interests';
import RequiredCurrencies from './required-currencies';
import { RegistrationInformationTypes } from './types';

import commonStyles from '../../common/styles.module.css';
import cn from '../styles.module.css';

const RegistrationInformation = ({
  control,
  status,
  clearErrors,
  isEditable,
  errors,
  regulatedCompanyScheme,
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  sicCodesList,
  setSicCodesList,
  fields,
  setValue,
  countriesOfInterestVisible,
  countriesOfInterestList,
  applicationScheme,
  setCountriesOfInterestList,
  countries,
  deliveryChannelsVisible,
  deliveryChannelsCorporate,
  setRequiredCurrenciesList,
  requiredCurrenciesList,
  currencies,
}: RegistrationInformationTypes) => (
  <>
    <StepPartBlock title="Registration information">
      <>
        <InfoRow
          title="Date of incorporation"
          id="dateOfIncorporation"
          lessPadding
        >
          <Controller
            control={control}
            name="dateOfIncorporation"
            defaultValue=""
            render={({ field: { onChange, value } }) => {
              let className;

              if (
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED
              ) {
                className = commonStyles.dateInputDisabled;

                if (value) {
                  className = `${commonStyles.dateInputDisabled} ${
                    value ? commonStyles.dateInputDisabledWithValue : ''
                  }`;
                }
              }
              return (
                <DateInput
                  value={value}
                  placeholder="Enter date of incorporation"
                  onChange={(date) => {
                    onChange(date.toISOString());
                    clearErrors(['dateOfIncorporation']);
                  }}
                  className={className}
                  size={DateInputSize.SMALL}
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={errors?.dateOfIncorporation?.message}
                  disabledAfter={new Date()}
                  borderless
                  withoutCustomSelector
                />
              );
            }}
          />
        </InfoRow>
        <InfoRow title="Company Number" id="companyNumber" lessPadding>
          <Controller
            control={control}
            name="companyNumber"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <InputText
                name="companyNumber"
                placeholder="Enter company number..."
                value={value}
                onChange={(e) => {
                  onChange(e);
                  clearErrors(['companyNumber']);
                }}
                size={InputSize.SMALL}
                className="borderless"
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
                tabIndex={
                  !isEditable &&
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                    ? -1
                    : 0
                }
                error={errors?.companyNumber?.message}
              />
            )}
          />
        </InfoRow>
        {regulatedCompanyScheme && (
          <InfoRow
            title="Legal Entity Identifier Code (optional)"
            id="legalEntityIdCode"
            lessPadding
          >
            <Controller
              control={questionnarieControl}
              name="legalEntityIdCode"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter code..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['legalEntityIdCode']);
                  }}
                  size={InputSize.SMALL}
                  className="borderless"
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={questionnarieErrors?.legalEntityIdCode?.message}
                />
              )}
            />
          </InfoRow>
        )}
        {sicCodesList?.map((item, index) => (
          <SicCodes
            // eslint-disable-next-line react/no-array-index-key
            key={`sic-${item.id}-${index}`}
            value={item}
            setValue={setValue}
            values={sicCodesList}
            setValuesList={setSicCodesList}
            index={index}
            disabled={
              !isEditable ||
              Boolean(
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED,
              )
            }
            error={errors?.sicCodes?.message}
            clearErrors={clearErrors}
          />
        ))}
        {isEditable &&
          status !== NewApplicationStatus.ACCEPTED &&
          status !== NewApplicationStatus.DECLINED && (
            <button
              type="button"
              className={cn.stepOneValueButton}
              onClick={() => {
                const nextSicCodes = (
                  [] as { id: string; name: string }[]
                ).concat(sicCodesList);

                nextSicCodes.push({ id: '', name: '' });

                setSicCodesList(nextSicCodes);
              }}
              tabIndex={-1}
            >
              <span>+</span> add one more SIC
            </button>
          )}
        <div className={commonStyles.separator} />
        {regulatedCompanyScheme && (
          <InfoRow
            title="Do the Customer’s business activities require a license / permit / registration?"
            id="requiresBusinessLicense"
            lessPadding
          >
            <Controller
              control={control}
              name="requiresBusinessLicense"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="requiresBusinessLicense"
                  placeholder="Select an option..."
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['requiresBusinessLicense']);
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? commonStyles.selectDisabled
                      : ''
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={errors?.requiresBusinessLicense?.message}
                  fullWidth
                  borderless
                />
              )}
            />
          </InfoRow>
        )}
        {((regulatedCompanyScheme &&
          fields?.requiresBusinessLicense === 'yes') ||
          !regulatedCompanyScheme) && (
          <InfoRow
            title={`Business license ${regulatedCompanyScheme ? 'number' : '(optional)'}`}
            id="businessLicense"
            className={commonStyles.noFlex}
            lessPadding
          >
            <Controller
              control={control}
              name="businessLicense"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Textarea
                  name="businessLicense"
                  placeholder="Enter license..."
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['businessLicense']);
                  }}
                  value={value}
                  className={`borderless ${commonStyles.stepOneTextarea}`}
                  error={errors?.businessLicense?.message}
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                />
              )}
            />
          </InfoRow>
        )}
        <InfoRow title="Link to the website" id="websiteLink" lessPadding>
          <Controller
            control={control}
            name="websiteLink"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <InputText
                name="websiteLink"
                placeholder="Enter link..."
                value={value}
                onChange={(e) => {
                  onChange(e);
                  clearErrors(['websiteLink']);
                }}
                size={InputSize.SMALL}
                className="borderless"
                error={errors?.websiteLink?.message}
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
                tabIndex={
                  !isEditable &&
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                    ? -1
                    : 0
                }
              />
            )}
          />
        </InfoRow>
        {countriesOfInterestVisible && (
          <>
            {countriesOfInterestList?.map((item, index) => (
              <CountriesOnInterests
                title={
                  applicationScheme &&
                  applicationScheme?.countriesOfInterestTitle
                    ? applicationScheme?.countriesOfInterestTitle
                    : 'Countries of interest'
                }
                // eslint-disable-next-line react/no-array-index-key
                key={`countriesOfInterestIds-${item.name}-${index}`}
                value={item}
                setValue={setValue}
                values={countriesOfInterestList}
                setValuesList={setCountriesOfInterestList}
                list={countries}
                index={index}
                clearErrors={clearErrors}
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
                error={
                  errors?.countriesOfInterestIds?.message ||
                  errors?.countriesOfInterest?.message
                }
              />
            ))}
            {isEditable &&
              status !== NewApplicationStatus.ACCEPTED &&
              status !== NewApplicationStatus.DECLINED && (
                <button
                  type="button"
                  className={cn.stepOneValueButton}
                  onClick={() => {
                    const nextCountriesOfInterest = (
                      [] as { id: string; name: string }[]
                    ).concat(countriesOfInterestList);

                    nextCountriesOfInterest.push({ id: '', name: '' });

                    setCountriesOfInterestList(nextCountriesOfInterest);
                  }}
                  tabIndex={-1}
                >
                  <span>+</span> add one more Countries
                </button>
              )}
            <div className={commonStyles.separator} />
          </>
        )}
        {applicationScheme?.vatNumberActive && (
          <InfoRow title="VAT Number" id="vatNumber" lessPadding>
            <Controller
              control={control}
              name="vatNumber"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <InputText
                  name="vatNumber"
                  placeholder="Enter VAT..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['vatNumber']);
                  }}
                  size={InputSize.SMALL}
                  className="borderless"
                  error={errors?.vatNumber?.message}
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                />
              )}
            />
          </InfoRow>
        )}
        <InfoRow
          title="Business description (optional)"
          id="businessDescription"
          className={commonStyles.noFlex}
          lessPadding
        >
          <Controller
            control={control}
            name="businessDescription"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Textarea
                name="businessDescription"
                placeholder="Enter desription..."
                onChange={(e) => {
                  onChange(e);
                  clearErrors(['businessDescription']);
                }}
                value={value}
                className={`borderless ${commonStyles.stepOneTextarea}`}
                error={errors?.businessDescription?.message}
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
                tabIndex={
                  !isEditable &&
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                    ? -1
                    : 0
                }
              />
            )}
          />
        </InfoRow>
        {deliveryChannelsVisible && (
          <InfoRow
            title={
              applicationScheme?.deliveryChannelTitle || 'Delivery channel'
            }
            id="deliveryChannelId"
            lessPadding
          >
            <Controller
              control={control}
              name="deliveryChannelId"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="deliveryChannelId"
                  placeholder="Select an option..."
                  list={deliveryChannelsCorporate}
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['deliveryChannelId']);
                  }}
                  selectWrapperClassName={
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED
                      ? commonStyles.selectDisabled
                      : ''
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  error={errors?.deliveryChannelId?.message}
                  borderless
                  fullWidth
                />
              )}
            />
          </InfoRow>
        )}
      </>
    </StepPartBlock>
    {(applicationScheme?.amlCtfComplianceProgramActive ||
      applicationScheme?.expectedTypeOfClientsActive ||
      applicationScheme?.purposeOfAccountActive ||
      applicationScheme?.requiredCurrenciesCryptoActive) && (
      <StepPartBlock title="Business & Compliance Profile">
        <>
          {applicationScheme?.purposeOfAccountActive && (
            <InfoRow
              title="Purpose of account"
              id="businessLicense"
              lessPadding
            >
              <Controller
                control={control}
                name="purposeOfAccount"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Textarea
                    name="purposeOfAccount"
                    placeholder="Describe Purpose of account"
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['purposeOfAccount']);
                    }}
                    value={value}
                    className={`borderless ${commonStyles.stepOneTextarea} ${commonStyles.purposeOfAccount}`}
                    error={errors?.purposeOfAccount?.message}
                    tabIndex={
                      !isEditable &&
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                  />
                )}
              />
            </InfoRow>
          )}
          {applicationScheme?.expectedTypeOfClientsActive && (
            <InfoRow
              title="Expected types of clients"
              id="expectedTypeOfClients"
              lessPadding
            >
              <Controller
                control={control}
                name="expectedTypeOfClients"
                defaultValue={[]}
                render={({ field: { onChange, value } }) => (
                  <Select
                    name="expectedTypeOfClients"
                    placeholder="Select types"
                    list={[
                      {
                        name: 'B2B',
                        value: 'B2B',
                      },
                      {
                        name: 'B2C',
                        value: 'B2C',
                      },
                    ]}
                    value={value}
                    size={SelectSize.SMALL}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['expectedTypeOfClients']);
                    }}
                    mode={SelectType.MULTIPLE}
                    selectWrapperClassName={
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED
                        ? commonStyles.selectDisabled
                        : ''
                    }
                    disabled={
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    tabIndex={
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    error={errors?.expectedTypeOfClients?.message}
                    borderless
                    fullWidth
                  />
                )}
              />
            </InfoRow>
          )}
          {applicationScheme?.requiredCurrenciesCryptoActive && (
            <>
              {requiredCurrenciesList?.map((item, index) => (
                <RequiredCurrencies
                  title="Required currency"
                  // eslint-disable-next-line react/no-array-index-key
                  key={`requiredCurrenciesCryptoId-${item.name}-${index}`}
                  value={item}
                  setValue={setValue}
                  values={requiredCurrenciesList}
                  setValuesList={setRequiredCurrenciesList}
                  list={currencies}
                  index={index}
                  clearErrors={clearErrors}
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  error={errors?.requiredCurrenciesCryptoId?.message}
                />
              ))}
              {isEditable &&
                status !== NewApplicationStatus.ACCEPTED &&
                status !== NewApplicationStatus.DECLINED && (
                  <button
                    type="button"
                    className={cn.stepOneValueButton}
                    onClick={() => {
                      const nextRequiredCurrencies = (
                        [] as { id: string; name: string }[]
                      ).concat(requiredCurrenciesList);

                      nextRequiredCurrencies.push({ id: '', name: '' });

                      setRequiredCurrenciesList(nextRequiredCurrencies);
                    }}
                    tabIndex={-1}
                  >
                    <span>+</span> add one more Currencies
                  </button>
                )}
            </>
          )}
          {applicationScheme?.amlCtfComplianceProgramActive && (
            <InfoRow
              title="Do you have a AML/CTF Compliance Programm"
              id="amlCtfComplianceProgram"
              lessPadding
            >
              <Controller
                control={control}
                name="amlCtfComplianceProgram"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    name="amlCtfComplianceProgram"
                    placeholder="Select an option..."
                    list={[
                      {
                        name: 'Yes',
                        value: 'yes',
                      },
                      {
                        name: 'No',
                        value: 'no',
                      },
                    ]}
                    value={value as string}
                    size={SelectSize.SMALL}
                    onChange={(e) => {
                      onChange(e);
                      clearErrors(['amlCtfComplianceProgram']);
                    }}
                    selectWrapperClassName={
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED
                        ? commonStyles.selectDisabled
                        : ''
                    }
                    disabled={
                      !isEditable ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    tabIndex={
                      !isEditable &&
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    error={errors?.amlCtfComplianceProgram?.message}
                    fullWidth
                    borderless
                  />
                )}
              />
            </InfoRow>
          )}
        </>
      </StepPartBlock>
    )}
  </>
);

export default RegistrationInformation;
